import { useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Drawer } from "@design-system/Drawer/Drawer";
import { DocumentName } from "@components/documents/DocumentName";
import { Separator } from "@design-system/Separator/Separator";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { DocWithTaxonomy } from "./documentRetrievalHelpers";
import {
    getDocVerificationStatus,
    isRetryableVerification,
    toExpectedCategorySet,
} from "./documentVerificationStatus";
import {
    getVerificationDisplay,
    getVerificationDescription,
} from "./documentReviewStatus";
import { TaxonomyReviewDrawerContent } from "./TaxonomyReviewDrawerContent";
import styles from "./DocumentRetrievalTaskBody.module.scss";

interface DocumentReviewDrawerProps {
    previewedDoc: DocWithTaxonomy | null;
    monthKeys: Set<string>;
    occupiedMonths: Map<string, number>;
    expectedCategoryIds?: string[];
    expectedAccountNumberCandidates?: readonly string[];
    expectedAccountNumberCandidatesLoading?: boolean;
    onClose: () => void;
    onRetry?: (documentId: number) => void;
    isRetrying?: boolean;
    onRemoveFromTask?: (documentId: number) => void;
    isRemoving?: boolean;
    onPeriodStartDateOverride?: (docId: number, value: string | null) => void;
    onPeriodEndDateOverride?: (docId: number, value: string | null) => void;
}

export const DocumentReviewDrawer = ({
    previewedDoc,
    monthKeys,
    occupiedMonths,
    expectedCategoryIds,
    expectedAccountNumberCandidates,
    expectedAccountNumberCandidatesLoading,
    onClose,
    onRetry,
    isRetrying,
    onRemoveFromTask,
    isRemoving,
    onPeriodStartDateOverride,
    onPeriodEndDateOverride,
}: DocumentReviewDrawerProps) => {
    const [localPeriodStartDate, setLocalPeriodStartDate] = useState<
        string | null
    >(null);
    const [localPeriodEndDate, setLocalPeriodEndDate] = useState<string | null>(
        null,
    );
    const prevDocIdRef = useRef<number | null | undefined>(previewedDoc?.id);
    if (prevDocIdRef.current !== previewedDoc?.id) {
        prevDocIdRef.current = previewedDoc?.id;
        setLocalPeriodStartDate(null);
        setLocalPeriodEndDate(null);
    }

    const expectedCategorySet = useMemo(
        () => toExpectedCategorySet(expectedCategoryIds),
        [expectedCategoryIds],
    );

    const verification = useMemo(() => {
        if (!previewedDoc) {
            return null;
        }
        const hasLocalOverride =
            localPeriodStartDate !== null || localPeriodEndDate !== null;
        const docForVerification: DocWithTaxonomy = hasLocalOverride
            ? {
                  ...previewedDoc,
                  taxonomyExtraction: {
                      ...(previewedDoc.taxonomyExtraction ?? {
                          periodStartDate: null,
                          periodEndDate: null,
                          accountNumber: null,
                          institutionName: null,
                          otherAccountIdentifiers: null,
                      }),
                      ...(localPeriodStartDate !== null && {
                          periodStartDate: localPeriodStartDate || null,
                      }),
                      ...(localPeriodEndDate !== null && {
                          periodEndDate: localPeriodEndDate || null,
                      }),
                  },
              }
            : previewedDoc;
        return getDocVerificationStatus(docForVerification, {
            monthKeys,
            occupiedMonths,
            expectedCategoryIds: expectedCategorySet,
            expectedAccountNumberCandidates,
            expectedAccountNumberCandidatesLoading,
        });
    }, [
        previewedDoc,
        localPeriodStartDate,
        localPeriodEndDate,
        monthKeys,
        occupiedMonths,
        expectedCategorySet,
        expectedAccountNumberCandidates,
        expectedAccountNumberCandidatesLoading,
    ]);

    const statusConfig = verification
        ? getVerificationDisplay(verification)
        : null;
    const statusDescription = verification
        ? getVerificationDescription(verification)
        : null;
    const isRetryable =
        !!verification && isRetryableVerification(verification) && !!onRetry;
    const hasMenuItems = isRetryable || !!onRemoveFromTask;

    const handlePeriodStartDateChange = useCallback(
        (value: string) => {
            setLocalPeriodStartDate(value);
            if (previewedDoc) {
                onPeriodStartDateOverride?.(previewedDoc.id, value || null);
            }
        },
        [previewedDoc, onPeriodStartDateOverride],
    );

    const handlePeriodEndDateChange = useCallback(
        (value: string) => {
            setLocalPeriodEndDate(value);
            if (previewedDoc) {
                onPeriodEndDateOverride?.(previewedDoc.id, value || null);
            }
        },
        [previewedDoc, onPeriodEndDateOverride],
    );

    return (
        <Drawer
            open={!!previewedDoc}
            direction="right"
            modal={false}
            size="half"
            dismissible={false}
            headerClassName={styles.drawerHeader}
            headerAddonLeft={
                previewedDoc && (
                    <DocumentName
                        fileName={previewedDoc.fileName}
                        icon
                        documentId={previewedDoc.id}
                        textClassName={styles.drawerDocumentName}
                    />
                )
            }
            headerAddonBeforeClose={
                (!!statusConfig || hasMenuItems) && (
                    <div className={styles.drawerHeaderRight}>
                        {statusConfig &&
                            (statusDescription ? (
                                <Tooltip
                                    content={statusDescription}
                                    side="bottom"
                                >
                                    <div
                                        className={classNames(
                                            styles.drawerHeaderStatus,
                                            statusConfig.className,
                                        )}
                                    >
                                        {statusConfig.icon}
                                        <span>{statusConfig.label}</span>
                                    </div>
                                </Tooltip>
                            ) : (
                                <div
                                    className={classNames(
                                        styles.drawerHeaderStatus,
                                        statusConfig.className,
                                    )}
                                >
                                    {statusConfig.icon}
                                    <span>{statusConfig.label}</span>
                                </div>
                            ))}
                        {hasMenuItems && previewedDoc && (
                            <Menu.More
                                size="md"
                                align="start"
                                aria-label="Document actions"
                            >
                                {isRetryable && (
                                    <Menu.Item
                                        onClick={() =>
                                            onRetry?.(previewedDoc.id)
                                        }
                                        disabled={isRetrying || isRemoving}
                                    >
                                        {isRetrying
                                            ? "Retrying..."
                                            : "Retry extraction"}
                                    </Menu.Item>
                                )}
                                {onRemoveFromTask && (
                                    <Menu.Item
                                        destructive
                                        disabled={isRemoving || isRetrying}
                                        onClick={() =>
                                            onRemoveFromTask(previewedDoc.id)
                                        }
                                    >
                                        {isRemoving
                                            ? "Removing..."
                                            : "Remove from task"}
                                    </Menu.Item>
                                )}
                            </Menu.More>
                        )}
                        {(!!statusConfig || hasMenuItems) && (
                            <Separator
                                orientation="vertical"
                                className={styles.drawerHeaderSeparator}
                            />
                        )}
                    </div>
                )
            }
            onClose={onClose}
            noPadding
        >
            {previewedDoc && (
                <TaxonomyReviewDrawerContent
                    key={previewedDoc.id}
                    documentId={previewedDoc.id}
                    fileName={previewedDoc.fileName}
                    taxonomyExtractionStatus={
                        previewedDoc.taxonomyExtractionStatus
                    }
                    taxonomyExtraction={previewedDoc.taxonomyExtraction}
                    documentCategory={previewedDoc.documentCategory ?? null}
                    invalidFields={
                        verification?.kind === "extracting"
                            ? undefined
                            : verification?.invalidFields
                    }
                    onPeriodStartDateChange={handlePeriodStartDateChange}
                    onPeriodEndDateChange={handlePeriodEndDateChange}
                />
            )}
        </Drawer>
    );
};
